// Logica PURA della coda multi-upload (Memory Queue, Batch A).
//
// Nessun side-effect, nessun accesso al DOM o alla rete: input → output
// deterministico, testabile in isolamento (vedi scripts/test-frontend-pure.mjs),
// stesso pattern di `format.ts` / `upload-view.ts`. Il wiring DOM resta in
// `main.ts` e delega a queste funzioni.
//
// Vincoli di scope garantiti qui:
//  - cap MAX_BATCH foto per batch;
//  - TUTTI i metadati sono PER-FOTO di default (coordinate, data, descrizione,
//    categorie, nome luogo, privacy): nessun valore comune applicato in automatico;
//  - la condivisione tra foto avviene SOLO via "Mantieni per tutte le foto"
//    (KeepFlags), esplicita e per-campo, e NON include MAI GPS, data o gender;
//  - NESSUN campo gender (i tag private-only non sono mai applicati in bulk);
//  - nessun raw EXIF / device / camera: si propagano solo coords, data, dimensioni.

/** Numero massimo di foto per batch (cap deciso per Batch A). */
export const MAX_BATCH = 10;

export type QueueStatus =
  | "pending" // in coda; caricabile solo se ha coordinate
  | "uploading"
  | "done"
  | "error"
  | "unsupported"; // HEIC/HEIF: non caricabile (nessuna conversione in Batch A)

export interface QueueItem {
  uid: string;
  name: string;
  status: QueueStatus;
  error: string | null;
  // Coordinate PER-FOTO (da EXIF o impostate a mano sulla mappa). Mai bulk.
  lat: number | null;
  lng: number | null;
  // Data scatto PER-FOTO come valore <input type="datetime-local"> (mai bulk).
  takenAt: string | null;
  // true se l'EXIF ha precompilato coordinate e/o data (→ exif_source backend).
  exifApplied: boolean;
  // Dimensioni lette client-side dalle natural dimensions (NON da EXIF).
  width: number | null;
  height: number | null;
  heic: boolean;
  // Metadati PER-FOTO (default per-foto; condivisi solo via KeepFlags espliciti).
  description: string | null;
  categories: string[];
  placeLabel: string | null;
  isPrivate: boolean;
}

/**
 * Flag "Mantieni per tutte le foto": per-campo, espliciti, default OFF.
 * Insieme volutamente RISTRETTO: GPS, data e gender non sono mai inclusi.
 */
export interface KeepFlags {
  categories: boolean;
  privacy: boolean;
  placeLabel: boolean;
  description: boolean;
}

export const NO_KEEP: KeepFlags = {
  categories: false,
  privacy: false,
  placeLabel: false,
  description: false,
};

/** Sottoinsieme dei campi propagabili (mai GPS/data/gender). */
export type KeptValues = Partial<Pick<QueueItem, "description" | "categories" | "placeLabel" | "isPrivate">>;

export const HEIC_MESSAGE =
  "Formato HEIC/HEIF non supportato. Su iPhone: Impostazioni → Fotocamera → Formati → Massima compatibilità.";

/** Slot ancora disponibili nel batch (cap MAX_BATCH). */
export function remainingSlots(currentCount: number, max: number = MAX_BATCH): number {
  return Math.max(0, max - currentCount);
}

/** Crea un item di coda. `heic` è deciso dal chiamante (riusa `isHeic`). */
export function createQueueItem(uid: string, name: string, heic: boolean): QueueItem {
  return {
    uid,
    name,
    status: heic ? "unsupported" : "pending",
    error: heic ? HEIC_MESSAGE : null,
    lat: null,
    lng: null,
    takenAt: null,
    exifApplied: false,
    width: null,
    height: null,
    heic,
    description: null,
    categories: [],
    placeLabel: null,
    isPrivate: false,
  };
}

/** True se l'item è caricabile ORA: non HEIC, non già caricata/in corso, con coordinate. */
export function canUpload(item: QueueItem): boolean {
  return (
    !item.heic &&
    (item.status === "pending" || item.status === "error") &&
    item.lat !== null &&
    item.lng !== null
  );
}

/** True se manca solo la posizione (per la messaggistica UX). */
export function needsCoordinates(item: QueueItem): boolean {
  return !item.heic && (item.status === "pending" || item.status === "error") && (item.lat === null || item.lng === null);
}

export interface QueueSummary {
  total: number;
  uploadable: number;
  done: number;
  error: number;
  unsupported: number;
  missingCoords: number;
}

export function summarize(items: QueueItem[]): QueueSummary {
  let uploadable = 0;
  let done = 0;
  let error = 0;
  let unsupported = 0;
  let missingCoords = 0;
  for (const it of items) {
    if (it.status === "done") done++;
    else if (it.status === "unsupported") unsupported++;
    else if (it.status === "error") error++;
    if (canUpload(it)) uploadable++;
    if (needsCoordinates(it)) missingCoords++;
  }
  return { total: items.length, uploadable, done, error, unsupported, missingCoords };
}

/**
 * Campi FormData (stringhe) per l'upload di un singolo item: TUTTI i metadati
 * vengono dall'item stesso (per-foto). L'eventuale condivisione è già stata
 * applicata all'item via `applyKept` PRIMA dell'upload.
 *
 * GARANZIE (scope):
 *  - `latitude`/`longitude`/`takenAt` per-foto;
 *  - NESSUN campo gender (`tagMale`/`tagFemale`): mai applicato in bulk;
 *  - `width`/`height` inclusi solo se presenti;
 *  - `exifSource` solo se l'EXIF ha precompilato qualcosa.
 *
 * Ritorna `null` se l'item non è caricabile (coordinate mancanti / heic / done).
 */
export function uploadFieldsFor(item: QueueItem): Record<string, string> | null {
  if (!canUpload(item)) return null;
  const fields: Record<string, string> = {
    latitude: String(item.lat),
    longitude: String(item.lng),
    categories: JSON.stringify(item.categories ?? []),
  };
  if (item.description) fields.description = item.description;
  if (item.placeLabel) fields.placeLabel = item.placeLabel;
  if (item.isPrivate) fields.isPrivate = "true";
  if (item.takenAt) fields.takenAt = item.takenAt;
  if (item.width !== null) fields.width = String(item.width);
  if (item.height !== null) fields.height = String(item.height);
  if (item.exifApplied) fields.exifSource = "true";
  return fields;
}

/**
 * Estrae dalla foto sorgente i SOLI campi marcati come "mantieni per tutte".
 * `categories` viene clonato per non condividere il riferimento dell'array.
 * Mai GPS/data/gender (non sono nel tipo KeepFlags).
 */
export function keptValues(source: QueueItem, keep: KeepFlags): KeptValues {
  const out: KeptValues = {};
  if (keep.categories) out.categories = [...source.categories];
  if (keep.privacy) out.isPrivate = source.isPrivate;
  if (keep.placeLabel) out.placeLabel = source.placeLabel;
  if (keep.description) out.description = source.description;
  return out;
}

/**
 * Applica i valori "tenuti" a un item target (mutazione controllata). Non tocca
 * item HEIC o già caricati/in corso. `categories` clonato per target.
 */
export function applyKept(target: QueueItem, kept: KeptValues): void {
  if (target.heic || target.status === "done" || target.status === "uploading") return;
  if (kept.categories !== undefined) target.categories = [...kept.categories];
  if (kept.isPrivate !== undefined) target.isPrivate = kept.isPrivate;
  if (kept.placeLabel !== undefined) target.placeLabel = kept.placeLabel;
  if (kept.description !== undefined) target.description = kept.description;
}

/** Parsing prudente di `Retry-After` (secondi). `null` se assente/non valido. */
export function parseRetryAfterSeconds(headerValue: string | null | undefined): number | null {
  if (!headerValue) return null;
  const n = Number(String(headerValue).trim());
  return Number.isFinite(n) && n >= 0 ? Math.ceil(n) : null;
}
